'use client'

import { useRouter, usePathname } from 'next/navigation'
import React, { createContext, useContext, useEffect, useState } from 'react'

interface TelegramWebApp {
  initData: string
  initDataUnsafe: {
    user?: {
      id: number
      first_name: string
      last_name?: string
      username?: string
      language_code?: string
    }
    start_param?: string
  }
  version: string
  platform: string
  colorScheme: 'light' | 'dark'
  themeParams: Record<string, string>
  isExpanded: boolean
  viewportHeight: number
  viewportStableHeight: number
  ready(): void
  expand(): void
  close(): void
  showAlert(message: string, callback?: () => void): void
  MainButton: {
    text: string
    color: string
    textColor: string
    isVisible: boolean
    isActive: boolean
    show(): void
    hide(): void
    onClick(fn: () => void): void
    offClick(fn: () => void): void
  }
  BackButton: {
    isVisible: boolean
    show(): void
    hide(): void
    onClick(fn: () => void): void
    offClick(fn: () => void): void
  }
}

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp
    }
  }
}

interface TelegramContextValue {
  isTelegram: boolean
  webApp: TelegramWebApp | null
  telegramUser: TelegramWebApp['initDataUnsafe']['user'] | null
}

const TelegramContext = createContext<TelegramContextValue>({
  isTelegram: false,
  webApp: null,
  telegramUser: null,
})

export function useTelegram() {
  return useContext(TelegramContext)
}

/**
 * TelegramProvider
 *
 * - Detects if app is running inside Telegram Mini App
 * - Calls WebApp.ready() to hide the loading screen
 * - Calls WebApp.expand() to use the full height
 * - If running in Telegram AND user is not on /login or authenticated pages,
 *   redirects to /login so the Telegram user can sign in
 */
export function TelegramProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter()
  const pathname = usePathname()
  const [isTelegram, setIsTelegram] = useState(false)
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null)
  const [telegramUser, setTelegramUser] = useState<TelegramWebApp['initDataUnsafe']['user'] | null>(
    null,
  )

  useEffect(() => {
    const tg = window.Telegram?.WebApp

    if (tg && tg.initData) {
      setIsTelegram(true)
      setWebApp(tg)
      setTelegramUser(tg.initDataUnsafe?.user ?? null)

      // Signal to Telegram that the app is ready (hides the native loading screen)
      tg.ready()

      // Expand to full height
      tg.expand()

      // If we're in Telegram and on the root path, redirect to login
      if (pathname === '/') {
        router.replace('/login')
      }
    }
  }, [pathname, router])

  return (
    <TelegramContext.Provider value={{ isTelegram, webApp, telegramUser }}>
      {children}
    </TelegramContext.Provider>
  )
}
